import exposeToGlobalScope from './expose-to-global-scope';

interface RgbaColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

type FormatOption = { full?: boolean; short?: boolean };

const RGB_RE = /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/;

function parseRgbString(input: string): RgbaColor | null {
  const match = input.match(RGB_RE);
  if (!match) return null;
  return {
    r: Number.parseInt(match[1]!, 10),
    g: Number.parseInt(match[2]!, 10),
    b: Number.parseInt(match[3]!, 10),
    a: match[4] ? Number.parseFloat(match[4]) : 1,
  };
}

function parseHexString(input: string): RgbaColor | null {
  const hex = input.slice(1);
  const double = (c: string): number => Number.parseInt(c + c, 16);
  const byte = (start: number): number =>
    Number.parseInt(hex.slice(start, start + 2), 16);

  switch (hex.length) {
    case 3:
      return {
        r: double(hex[0]!),
        g: double(hex[1]!),
        b: double(hex[2]!),
        a: 1,
      };
    case 4:
      return {
        r: double(hex[0]!),
        g: double(hex[1]!),
        b: double(hex[2]!),
        a: double(hex[3]!) / 255,
      };
    case 6:
      return { r: byte(0), g: byte(2), b: byte(4), a: 1 };
    case 8:
      return { r: byte(0), g: byte(2), b: byte(4), a: byte(6) / 255 };
    default:
      return null;
  }
}

export default function colorToRgba(
  colorRaw: string | null | undefined,
  format?: FormatOption
): RgbaColor | string | null {
  const colorString = (colorRaw ?? '').toLowerCase().trim();
  if (!colorString) return null;

  const parsedColor = colorString.startsWith('#')
    ? parseHexString(colorString)
    : parseRgbString(colorString);

  if (!parsedColor) return null;

  // Guard against NaN components from both hex (invalid chars like "#XYZ")
  // and rgb/rgba (malformed alpha like "rgba(0,0,0,..)") parsing paths.
  const { r, g, b, a } = parsedColor;
  if ([r, g, b, a].some(Number.isNaN)) return null;

  if (format?.full) {
    return a < 1 ? `rgba(${r}, ${g}, ${b}, ${a})` : `rgb(${r}, ${g}, ${b})`;
  }
  if (format?.short) {
    return a < 1 ? `${r}, ${g}, ${b}, ${a}` : `${r}, ${g}, ${b}`;
  }
  return { r, g, b, a };
}

exposeToGlobalScope(colorToRgba, 'colorToRgba');
